import { useCallback } from 'react'
import { AccountSettingsPromiseClient } from '@trustero/trustero-api-web/lib/accountsettings/accountsettings_grpc_web_pb'
import { ScheduleManagerPromiseClient } from '@trustero/trustero-api-web/lib/schedulemanager/schedulemanager_grpc_web_pb'
import { NotificationPromiseClient } from '@trustero/trustero-api-web/lib/notification/notification_grpc_web_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { useGrpcRevalidateByMethod } from 'src/components'
import {
  DismissNotificationRequest,
  GetNotificationsRequest,
  GetNotificationsResponse,
  LatestNotificationRequest,
  LatestNotificationResponse,
  NotificationTypeFilter,
  NOTIFICATION_TYPE,
} from '@trustero/trustero-api-web/lib/notification/notification_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import {
  GetDateLimitResponse,
  UpdateDateLimitRequest,
  UpdateValidateImageTimestampsRequest,
  ValidateImageTimestamps,
} from '@trustero/trustero-api-web/lib/accountsettings/accountsettings_pb'
import { RiskServicePromiseClient } from '@trustero/trustero-api-web/lib/risk/risk_grpc_web_pb'
import { GetRiskCategoryOptionResponse } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { DeleteDepartmentRequest } from '@trustero/trustero-api-web/lib/model/model_pb'
import {
  Departments,
  CreateOrUpdateDepartmentRequest,
} from '@trustero/trustero-api-web/lib/model/department_pb'
import { useInvalidateAuditRunsCache } from '../AuditBot/AuditBot.hooks'

export const useInvalidateAccountSettings = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    await Promise.all([
      mutateFunc(AccountSettingsPromiseClient.prototype.getDateLimit),
      mutateFunc(ScheduleManagerPromiseClient.prototype.getSchedule),
      mutateFunc(
        AccountSettingsPromiseClient.prototype.getValidateImageTimestamps,
      ),
      mutateFunc(RiskServicePromiseClient.prototype.getRiskCategoryOption),
      mutateFunc(ModelPromiseClient.prototype.listDepartments),
    ])
  }, [mutateFunc])
}

export const useInvalidateNotifications = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    await Promise.all([
      mutateFunc(NotificationPromiseClient.prototype.getNotifications),
      mutateFunc(NotificationPromiseClient.prototype.getLatestNotification),
    ])
  }, [mutateFunc])
}

export const useDateLimit = (): GrpcResponse<GetDateLimitResponse> => {
  const { response } = useSwrImmutableGrpc(
    AccountSettingsPromiseClient.prototype.getDateLimit,
    new Empty(),
  )
  return NewGrpcResponse(response)
}

export const useEditDateLimit = (): ((dateLimit: Timestamp) => void) => {
  const client = useAuthorizedGrpcClient(AccountSettingsPromiseClient)
  const mutateAuditRuns = useInvalidateAuditRunsCache()
  const mutateDateLimit = useInvalidateAccountSettings()

  return async (dateLimit: Timestamp) => {
    const request = new UpdateDateLimitRequest().setDateLimit(dateLimit)
    await client.updateDateLimit(request)
    await mutateAuditRuns()
    await mutateDateLimit()
  }
}

export const useValidateImageTimestamp =
  (): GrpcResponse<ValidateImageTimestamps> => {
    const { response } = useSwrImmutableGrpc(
      AccountSettingsPromiseClient.prototype.getValidateImageTimestamps,
      new Empty(),
    )
    return NewGrpcResponse(response)
  }

export const useSetValidateImageTimestamp = (): ((toggle: boolean) => void) => {
  const client = useAuthorizedGrpcClient(AccountSettingsPromiseClient)
  const mutateAuditRuns = useInvalidateAuditRunsCache()
  const mutateValidateImageTimestamp = useInvalidateAccountSettings()

  return async (toggle: boolean) => {
    const request =
      new UpdateValidateImageTimestampsRequest().setValidateImageTimestamps(
        toggle,
      )
    await client.updateValidateImageTimestamps(request)
    await mutateAuditRuns()
    await mutateValidateImageTimestamp()
  }
}

export const useNotifications = (
  message?: string,
  types?: number[],
): GrpcResponse<GetNotificationsResponse> => {
  const request = new GetNotificationsRequest()
  message && request.setMessage(new StringValue().setValue(message))
  types &&
    request.setTypeFilter(new NotificationTypeFilter().setTypesList(types))
  const { response } = useSwrImmutableGrpc(
    NotificationPromiseClient.prototype.getNotifications,
    new GetNotificationsRequest(),
  )
  return NewGrpcResponse(response)
}

export const useLatestNotification = ({
  message,
  type,
}: {
  message: string
  type: NOTIFICATION_TYPE
  shouldFetch?: boolean
}): GrpcResponse<LatestNotificationResponse> => {
  const { response } = useSwrImmutableGrpc(
    NotificationPromiseClient.prototype.getLatestNotification,
    new LatestNotificationRequest().setMessage(message).setType(type),
  )
  return NewGrpcResponse(response)
}

export const useDismissNotification = (): (({
  type,
  message,
}: {
  type: NOTIFICATION_TYPE
  message: string
}) => void) => {
  const client = useAuthorizedGrpcClient(NotificationPromiseClient)
  const mutate = useInvalidateNotifications()

  return async ({ type, message }) => {
    await client.dismissNotification(
      new DismissNotificationRequest().setMessage(message).setType(type),
    )
    await mutate()
  }
}

export const useRiskCategories =
  (): GrpcResponse<GetRiskCategoryOptionResponse> => {
    const { response } = useSwrImmutableGrpc(
      RiskServicePromiseClient.prototype.getRiskCategoryOption,
      new Empty(),
    )
    return NewGrpcResponse(response)
  }

export const useDepartments = (): GrpcResponse<Departments> => {
  const { response } = useSwrImmutableGrpc(
    ModelPromiseClient.prototype.listDepartments,
    new Empty(),
  )
  return NewGrpcResponse(response)
}
export interface DepartmentOption {
  modelId: string
  name: string
  shortName: string
  isCustom?: boolean
}

export const useUpsertDepartments = (): ((
  departments: DepartmentOption[],
) => void) => {
  const client = useAuthorizedGrpcClient(ModelPromiseClient)

  return async (departments: DepartmentOption[]) => {
    for (const department of departments) {
      const request = new CreateOrUpdateDepartmentRequest()
      request.setModelId(department.modelId)
      request.setName(new StringValue().setValue(department.name))
      request.setShortName(
        new StringValue().setValue(department.shortName || ''),
      )
      request.setIsCustom(new BoolValue().setValue(true))
      request.setIsTemplate(new BoolValue().setValue(false))
      await client.createOrUpdateDepartment(request)
    }
  }
}

export const useDeleteDepartments = (): ((departmentIds: string[]) => void) => {
  const client = useAuthorizedGrpcClient(ModelPromiseClient)

  return async (departmentIds: string[]) => {
    for (const id of departmentIds) {
      const req = new DeleteDepartmentRequest()
      req.setDepartmentId(id)
      await client.deleteDepartment(req)
    }
  }
}
